"use client";
import * as React from "react";
import { useState } from "react";
import type { LoginProps } from "./types";
import getUniqueId from "../utils/getUniqueId";
import cssClassAssembler from "../utils/cssClassAssembler";
import defaultClasses from "./index.module.css";
const COMPONENT = "sso";
const DEFAULT_VALUES = {
  ssoIdentifier: "",
  inputLabel: "Tenant",
  placeholder: "",
  buttonText: "Sign-in with SSO",
};

function Login(props: LoginProps) {
  const [_ssoIdentifier, set_ssoIdentifier] = useState(
    () => DEFAULT_VALUES.ssoIdentifier
  );

  const [errMsg, setErrMsg] = useState(() => "");

  const [isProcessing, setIsProcessing] = useState(() => false);

  function isError() {
    return !!errMsg;
  }

  function disableButton() {
    return !(_ssoIdentifier || props.ssoIdentifier) || isProcessing;
  }

  function shouldRenderInput() {
    return !props.ssoIdentifier;
  }

  function InputId() {
    return getUniqueId(COMPONENT, "input");
  }

  function ErrorSpanId() {
    return getUniqueId(COMPONENT, "span");
  }

  function classes() {
    return {
      container: cssClassAssembler(
        props.classNames?.container,
        defaultClasses.container
      ),
      label: cssClassAssembler(props.classNames?.label, defaultClasses.label),
      input: cssClassAssembler(props.classNames?.input, defaultClasses.input),
      button: cssClassAssembler(
        props.classNames?.button,
        defaultClasses.button
      ),
    };
  }

  function handleChange(event: Event) {
    setErrMsg("");
    set_ssoIdentifier((event.currentTarget as HTMLInputElement)?.value);
  }

  function onSubmitButton(event: Event) {
    event.preventDefault();
    setIsProcessing(true);
    const ssoIdentifierToSubmit = (_ssoIdentifier || props.ssoIdentifier) ?? "";
    props.onSubmit({
      ssoIdentifier: ssoIdentifierToSubmit,
      cb: (err) => {
        setIsProcessing(false);
        if (err?.error.message) {
          setErrMsg(err.error.message);
        }
      },
    });
  }

  return (
    <div
      style={props.styles?.container}
      className={classes().container}
      {...props.innerProps?.container}
    >
      {shouldRenderInput() ? (
        <>
          <label
            htmlFor={InputId()}
            style={props.styles?.label}
            className={classes().label}
            {...props.innerProps?.label}
          >
            {props.inputLabel || DEFAULT_VALUES.inputLabel}
          </label>
          <input
            id={InputId()}
            value={_ssoIdentifier}
            placeholder={props.placeholder || DEFAULT_VALUES.placeholder}
            onInput={(event) => handleChange(event)}
            style={props.styles?.input}
            className={classes().input}
            aria-invalid={isError()}
            aria-describedby={ErrorSpanId()}
            {...props.innerProps?.input}
          />
          {isError() ? <span id={ErrorSpanId()}>{errMsg}</span> : null}
        </>
      ) : null}
      <button
        type="button"
        disabled={disableButton()}
        onClick={(event) => onSubmitButton(event)}
        style={props.styles?.button}
        className={classes().button}
        {...props.innerProps?.button}
      >
        {props.buttonText || DEFAULT_VALUES.buttonText}
      </button>
    </div>
  );
}

export default Login;
